import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import { gql, useQuery } from '@apollo/client'
import LoadingPage from 'components/pages/LoadingPage'
import ErrorPage from 'components/pages/ErrorPage'
import NotFoundPage from 'components/pages/NotFoundPage'
import PageLayout from 'components/pages/PageLayout'
import PageMeta from 'components/PageMeta'
import TruckInformationSection from 'components/HeroSection'
import { withApollo } from 'components/withApollo'
import { GetStaticPathsResult, NextPage } from 'next'
import createApolloClient from 'components/withApollo/createApolloClient'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import { truckMenuPaths } from 'components/pages/TruckMenuPage/types/truckMenuPaths'
import {
  truckMenuQuery,
  truckMenuQueryVariables
} from 'components/pages/TruckMenuPage/types/truckMenuQuery'
import pathFromUrl from 'utils/pathFromUrl'
import TruckInformationSectionContent from 'components/HeroSection/TruckInformationSectionContent'
import TruckDescription from 'components/pages/TruckPage/TruckDescription'
import TruckRatings from 'components/pages/TruckPage/TruckRatings'
import LinksSection from 'components/HeroSection/LinksSection'
import Link from 'next/link'
import { useRedirect } from 'components/RedirectProvider'
import TruckMenus from 'components/TruckMenus'

import TRUCK_MENU_QUERY from './truckMenuQuery'

type Params = {
  seoName: string
  menuId?: string
  itemIds?: string
  isCatering?: string
  showInactive?: string
}

type Props = {}

const TRUCK_MENU_PATHS_QUERY = gql`
  query truckMenuPaths {
    customerPaths {
      truckPaths
    }
  }
`

function TruckMenuPage() {
  const router = useRouter()
  const {
    seoName,
    menuId: menuIdParam,
    itemIds: itemIdsParam,
    isCatering
  } = router.query as Params
  const { redirect } = useRedirect()

  const { data, loading, error } = useQuery<
    truckMenuQuery,
    truckMenuQueryVariables
  >(TRUCK_MENU_QUERY, {
    variables: {
      seoName
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  })
  const truck = data?.truck

  useEffect(() => {
    if (!truck?.slug) return

    if (truck.slug !== seoName) {
      router.replace(pathFromUrl(truck.menuUrl))
    }
  }, [truck, router, seoName])

  if (loading && !truck) return <LoadingPage />
  const notFoundError =
    error &&
    error.message.includes(
      'Cannot return null for non-nullable field Query.truck'
    )
  if (error && !notFoundError) return <ErrorPage message={error.message} />

  const isActive = truck?.active && truck?.restaurant?.active

  if (truck?.redirectToTruck) {
    return redirect({
      destination: truck.redirectToTruck.menuPath,
      permanent: true
    })
  }
  if (truck?.showAsArchived) {
    return redirect({
      destination: truck.scheduleUrl,
      permanent: true
    })
  }

  if (!data || !truck || notFoundError) {
    return <NotFoundPage search={seoName} />
  }

  if (!isActive && !truck.showAsArchived) {
    return <NotFoundPage search={seoName} />
  }

  const heroLinks = {
    twitter: truck.twitter,
    facebook: truck.facebook,
    instagram: truck.instagram,
    tiktok: truck.tiktok,
    website: truck.website
  }

  const activePublicMenus = truck.menus.filter(
    (m) => m.active && m.public && m.items.length > 0
  )

  return (
    <PageLayout highlightCartItems={false} fullWidth staticPage>
      <PageMeta
        canonicalPath={pathFromUrl(truck.menuUrl)}
        description={`${truck.name}'s menu`}
        image={truck.foodImage ? truck.foodImage.medium : truck.image.medium}
        ldJson={data.structuredData.truckMenuPage}
        title={`Best Food Trucks | ${truck.name} - menu`}
        url={truck.menuUrl}
      />

      <div className="w-full">
        <TruckInformationSection
          content={
            <TruckInformationSectionContent
              actions={
                isCatering
                  ? []
                  : ['bookTruck', 'viewSchedule', 'socialMedia', 'market']
              }
              heroLinks={heroLinks}
              truck={truck}
            />
          }
          imageSrc={truck.image.original!}
          links={<LinksSection socialLinks={heroLinks} />}
          size="small"
        />
        {activePublicMenus && (
          <TruckMenus
            itemIdsParam={itemIdsParam}
            menuIdParam={menuIdParam}
            showLinkToSchedule={!isCatering}
            truckId={truck.id}
          />
        )}

        {!activePublicMenus && (
          <div className="pt-2 px-2 pb-12">
            <h2 className="my-8 text-3xl tracking-wide font-bold text-center">
              {`${truck.name} Menu`}
            </h2>

            <div className="text-center italic w-full text-gray-400 text-xl mx-auto my-12">
              <p>This food truck has not posted any public menus.</p>
              {!truck.showAsArchived && (
                <p className="mt-2">
                  <Link
                    className="underline text-orange-700 hover:text-orange-600"
                    href={{
                      pathname: '/hire-food-truck',
                      query: {
                        truckId: truck.id
                      }
                    }}
                  >
                    Book this truck
                  </Link>{' '}
                  for a private event.
                </p>
              )}
            </div>
          </div>
        )}

        {truck.description?.html && (
          <TruckDescription
            description={truck.description?.html}
            name={truck.name}
            isGray
          />
        )}
        {truck.ratingInfo && truck.ratingInfo.ratings.length > 0 && (
          <TruckRatings isGray={false} ratingInfo={truck.ratingInfo} />
        )}
      </div>
    </PageLayout>
  )
}

export default withApollo({ ssr: false })(TruckMenuPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<truckMenuPaths>({
    query: TRUCK_MENU_PATHS_QUERY
  })

  const paths = data.customerPaths.truckPaths.map((truckPath) => ({
    params: { seoName: truckPath }
  }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(
  TruckMenuPage,
  {
    revalidate: 60 * 15 // 15 minutes
  }
)
